import Loader from '../../common/Loader'
import { Link } from 'react-router'
import { useOrgAuth } from '../../OrgAuthProvider.ts'
import { useGetOrganizationUsers } from '../../http/hooks.ts'
import { IUser } from '../../services/nextpeakClient'
import { Page, PageProps } from '../../layout/Page.tsx'
import Table, { validateTableProps } from '../../newComponents/Table/index.tsx'
import { TableDataCell, TableHeaderCell } from '../../newComponents/Table/Cell.tsx'
import Badge from '../../newComponents/Badge/index.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'

const EmployeesTable = ({ employees }: { employees: any[] }) => {
	const tableProps = validateTableProps(
		[
			{
				key: 'name',
				content: <TableHeaderCell>Name</TableHeaderCell>,
			},
			{
				key: 'email',
				content: <TableHeaderCell>Email</TableHeaderCell>,
			},
			{
				key: 'role',
				content: <TableHeaderCell>Role</TableHeaderCell>,
			},
			{
				key: 'action',
				content: <TableHeaderCell>Action</TableHeaderCell>,
			},
		] as const,
		employees.map((employee) => {
			const getRoleBadge = (employee: IUser) => {
				const mappedRoles: any[] = employee.roles!.map((role) => role.name)
				const badges = []
				if (mappedRoles.includes('admin')) {
					badges.push(
						<Badge variant="light" color="info">
							admin
						</Badge>
					)
				}
				if (mappedRoles.includes('manager')) {
					badges.push(
						<Badge variant="light" color="warning">
							manager
						</Badge>
					)
					return badges
				}
				badges.push(
					<Badge variant="light" color="success">
						employee
					</Badge>
				)
				return badges
			}

			return {
				name: <TableDataCell>{employee.name}</TableDataCell>,
				email: <TableDataCell>{employee.email}</TableDataCell>,
				role: <TableDataCell>{...getRoleBadge(employee)}</TableDataCell>,
				action: (
					<TableDataCell>
						<Link className="hover:text-primary" to={'/employees/' + employee.id}>
							<svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M8.99981 14.8219C3.43106 14.8219 0.674805 9.50624 0.562305 9.28124C0.47793 9.11249 0.47793 8.88749 0.562305 8.71874C0.674805 8.49374 3.43106 3.20624 8.99981 3.20624C14.5686 3.20624 17.3248 8.49374 17.4373 8.71874C17.5217 8.88749 17.5217 9.11249 17.4373 9.28124C17.3248 9.50624 14.5686 14.8219 8.99981 14.8219ZM1.85605 8.99999C2.4748 10.0406 4.89356 13.5562 8.99981 13.5562C13.1061 13.5562 15.5248 10.0406 16.1436 8.99999C15.5248 7.95936 13.1061 4.44374 8.99981 4.44374C4.89356 4.44374 2.4748 7.95936 1.85605 8.99999Z"
									fill=""
								/>
								<path
									d="M9 11.3906C7.67812 11.3906 6.60938 10.3219 6.60938 9C6.60938 7.67813 7.67812 6.60938 9 6.60938C10.3219 6.60938 11.3906 7.67813 11.3906 9C11.3906 10.3219 10.3219 11.3906 9 11.3906ZM9 7.875C8.38125 7.875 7.875 8.38125 7.875 9C7.875 9.61875 8.38125 10.125 9 10.125C9.61875 10.125 10.125 9.61875 10.125 9C10.125 8.38125 9.61875 7.875 9 7.875Z"
									fill=""
								/>
							</svg>
						</Link>
					</TableDataCell>
				),
			}
		})
	)

	return (
		<Card>
			<Table {...tableProps} />
		</Card>
	)
}

const pageProps: PageProps = {
	title: 'Organization employees',
	breadcrumbs: 'Organization employees',
}

const EmployeesWithOrgContext = ({ activeOrganizationId }: { activeOrganizationId: string }) => {
	const client = useNextPeakClient()
	const { data: employees } = useGetOrganizationUsers(
		client,
		{ organizationId: activeOrganizationId, includes: ['organizationRoles'] },
		{
			enabled: !!activeOrganizationId,
		}
	)

	return (
		<Page {...pageProps}>
			<div className="flex flex-col gap-10">
				{!employees && (
					<Card>
						<Loader />
					</Card>
				)}
				{employees && <EmployeesTable employees={employees} />}
			</div>
		</Page>
	)
}

const EmployeesList = () => {
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()

	if (!orgId) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>You need to select an active organization first</div>
				</div>
			</Page>
		)
	}

	return <EmployeesWithOrgContext activeOrganizationId={orgId} />
}

export default EmployeesList
